// Display helpers for entry passes (TRV14) — experience-category labels, order
// status copy, and the user-facing cancel-reason options.
import type { BadgeVariant } from './format';

/** Curated discovery categories (ONDC TRV14 experience codes). */
export const PASS_CATEGORIES: { code: string; label: string }[] = [
  { code: 'CULTURE_HERITAGE', label: 'Heritage' },
  { code: 'MUSEUM', label: 'Museums' },
  { code: 'CONCERT', label: 'Concerts' },
  { code: 'ENTERTAINMENT_SHOWS', label: 'Shows' },
  { code: 'TOURS_SIGHTSEEING', label: 'Tours' },
  { code: 'LEISURE_THEME_PARKS', label: 'Theme parks' },
  { code: 'SPORTING_OUTDOOR', label: 'Sports & outdoors' },
  { code: 'FOOD_DINING', label: 'Food & dining' },
];

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  PASS_CATEGORIES.map((c) => [c.code, c.label])
);

/** Friendly label for an experience category code (falls back to title-cased code). */
export function categoryLabel(code?: string): string {
  if (!code) return 'Entry pass';
  if (CATEGORY_LABELS[code]) return CATEGORY_LABELS[code];
  return code
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

/** Short copy for an entry-pass order status. */
export function passStatusText(status?: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'COMPLETED':
      return 'Visited';
    case 'CANCELLED':
      return 'Cancelled';
    case 'SOFT_CANCEL':
      return 'Cancelling';
    default:
      return status ?? '';
  }
}

/** Badge variant for a ticket credential's claim status (UNCLAIMED/CLAIMED). */
export function ticketStatusVariant(status?: string): BadgeVariant {
  switch (status) {
    case 'CLAIMED':
      return 'green';
    case 'UNCLAIMED':
      return 'amber';
    default:
      return 'default';
  }
}

/** Default rating category for an entry-pass order (TRV14 uses ORDER, not DRIVER). */
export const PASS_RATE_CATEGORY = 'ORDER';

/** User-facing cancel reason codes (TRV14 entry-pass set; default 001). */
export const PASS_CANCEL_REASONS: { code: string; label: string }[] = [
  { code: '001', label: 'Change of plans' },
  { code: '003', label: 'Booked the wrong date or time' },
  { code: '005', label: 'Booked the wrong experience' },
  { code: '004', label: 'Duplicate booking' },
  { code: '002', label: 'Personal emergency' },
];
